// Bylines for the reviewed commercial pages.
//
// The hub pages carry a "Last reviewed / who reviewed it" line, and the same
// person has to appear in the on-page byline and in <meta name="author">. That
// is two places on every page in the series, so the name lives in one JSON
// file per author under content/authors/ and is read through here — nothing
// hardcodes a person's name in a template.
//
// `name` is deliberately empty until the role is filled: publishing a made-up
// person is worse than publishing the team. Set `name` in the JSON file and
// every page in the series picks it up on the next build; nothing else needs
// to change.
import underwritingLead from '@/content/authors/underwriting-lead.json'

const AUTHORS = {
  'underwriting-lead': underwritingLead,
}

export const DEFAULT_AUTHOR_ID = 'underwriting-lead'

// Returns a byline-ready author, or null for an id we don't publish — a
// missing author must render nothing rather than "undefined · undefined".
export function getAuthor(id = DEFAULT_AUTHOR_ID) {
  const author = AUTHORS[id]
  if (!author) {
    return null
  }

  const name = (author.name || '').trim() || author.fallbackName

  return {
    id: author.id,
    name,
    jobTitle: author.jobTitle,
    url: author.url,
    // True once a real person has been assigned, so a future page can decide
    // to link the byline to a profile rather than the about page.
    isNamed: Boolean((author.name || '').trim()),
  }
}

// "19 August 2026" — the format the byline shows. Built from the parts rather
// than toLocaleString so the server and the browser cannot disagree about the
// locale and re-render a different string after hydration.
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export function formatReviewDate(isoDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDate || ''))
  if (!match) {
    return ''
  }
  const [, year, month, day] = match
  const monthName = MONTHS[Number(month) - 1]
  if (!monthName) {
    return ''
  }

  return `${Number(day)} ${monthName} ${year}`
}
